import React, { useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Globe, BookOpen, ArrowRight, AlertCircle } from "lucide-react";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import StickyBottomNav from "../../../components/Navbar/StickyBottomNav";

const ResourceCard = ({ organization, links, onExpand, isExpanded }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -5 }}
      onClick={onExpand}
      className="w-full bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
    >
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Globe className="w-5 h-5 text-primary" />
            </div>
            <h3 className="font-display text-h3 text-secondary">
              {organization}
            </h3>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.2 }}
            className="w-8 h-8 rounded-full bg-lightTeal flex items-center justify-center"
          >
            <ArrowRight className="w-4 h-4 text-primary" />
          </motion.div>
        </div>

        <motion.div
          initial={false}
          animate={{
            height: isExpanded ? "auto" : 0,
            opacity: isExpanded ? 1 : 0,
            marginTop: isExpanded ? 16 : 0
          }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <div className="space-y-4 pl-14">
            {links.map((link, index) => (
              <motion.div
                key={index}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-2 group"
                onClick={(e) => e.stopPropagation()} // Prevent card collapse when clicking links
              >
                <div className="w-6 h-6 rounded-full bg-lightTeal flex items-center justify-center">
                  <ExternalLink className="w-3 h-3 text-primary group-hover:text-secondary transition-colors duration-200" />
                </div>
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-textPrimary hover:text-primary text-bodyRegular transition-colors duration-200 break-all"
                >
                  {link}
                </a>
              </motion.div>
            ))}
          </div>
          <div className="w-16 h-1 bg-cta mt-6 ml-14"></div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const Quote = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8 }}
    className="relative w-full max-w-4xl mx-auto px-6 py-12"
  >
    <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
      <div className="absolute -top-20 -left-20 w-40 h-40 lg:w-56 lg:h-56 rounded-full bg-primary opacity-5" />
      <div className="absolute bottom-1/4 -right-20 w-40 h-40 lg:w-56 lg:h-56 rounded-full bg-cta opacity-5" />
    </div>

    <div className="relative bg-white rounded-xl shadow-md p-8 md:p-12">
      <div className="w-16 h-16 rounded-full bg-lightTeal flex items-center justify-center mb-6 mx-auto">
        <BookOpen className="w-8 h-8 text-primary" />
      </div>
      <blockquote className="text-2xl md:text-3xl font-display text-secondary mb-4 text-center">
        "Education is the most powerful weapon which you can use to change the
        world."
      </blockquote>
      <cite className="block text-textPrimary text-center text-lg">— Nelson Mandela</cite>
      <div className="w-16 h-1 bg-cta mx-auto mt-6"></div>
    </div>
  </motion.div>
);

const Africa = () => {
  const [expandedCard, setExpandedCard] = useState(null);

  const resources = [
    {
      organization: "Aljazeera",
      links: [
        "https://www.aljazeera.com/news/2019/2/9/what-you-need-to-know-about-africas-refugees"
      ]
    },
    {
      organization: "UNHCR",
      links: [
        "https://www.unhcr.org/us/about-unhcr/where-we-work/africa#:~:text=Sub%2DSaharan%20Africa%20hosts%20more,)%2C%20Nigeria%20and%20South%20Sudan"
      ]
    },
    {
      organization: "UN",
      links: [
        "https://www.un.org/africarenewal/magazine/december-2016-march-2017/africa-most-affected-refugee-crisis",
        "https://www.un.org/africarenewal/news/forced-displacement-we-can-learn-africa",
        "https://www.unrefugees.org/news/south-sudan-refugee-crisis-explained/"
      ]
    },
    {
      organization: "Migration Policy",
      links: [
        "https://www.migrationpolicy.org/article/forgotten-crisis-displacement-central-african-republic"
      ]
    },
    {
      organization: "World Vision",
      links: [
        "https://www.worldvision.org/refugees-news-stories/south-sudan-conflict-facts"
      ]
    },
    {
      organization: "BBC",
      links: ["https://www.bbc.com/news/world-africa-14095300"]
    },
    {
      organization: "Vox",
      links: [
        "https://www.vox.com/2020/9/11/21431157/trump-refugee-admissions-resettlement-cap-2021"
      ]
    },
    {
      organization: "Human Rights Watch",
      links: [
        "https://www.hrw.org/news/2020/12/21/sudan-security-forces-kill-protesters-eastern-sudan",
        "https://www.hrw.org/world-report/2020/country-chapters/sudan"
      ]
    },
    {
      organization: "CARE",
      links: [
        "https://care.org/our-work/disaster-response/emergencies/sudan-humanitarian-crisis/#:~:text=Civilians%20have%20suffered%20greatly%20from,200%2D250%20percent%20during%202019."
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-lightTeal">
      <Navbar />
      
      <div className="py-16 px-6 md:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-display text-h1 text-secondary mb-4">African Refugee Crisis</h1>
            <p className="text-bodyLarge text-textPrimary max-w-2xl mx-auto mb-4">
              Educational resources to better understand the refugee situation in Africa
            </p>
            <div className="w-16 h-1 bg-cta mx-auto"></div>
          </motion.div>
          
          <Quote />

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="max-w-4xl mx-auto px-4 py-12"
          >
            <div className="mb-8">
              <h2 className="font-display text-h2 text-secondary mb-4">Resource Links</h2>
              <p className="text-bodyRegular text-textPrimary mb-6">
                Below you'll find a curated list of resources from reputable organizations that provide information, data, and analysis about the refugee situation in Africa. Click on any organization to expand and see available links.
              </p>
              
              {resources.length === 0 ? (
                <div className="bg-white rounded-xl p-8 shadow-md text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
                    <AlertCircle size={32} className="text-red-500" />
                  </div>
                  <h3 className="font-display text-h3 text-secondary mb-2">No Resources Available</h3>
                  <p className="text-textPrimary">Resources will be added soon. Please check back later.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {resources.map((resource, index) => (
                    <ResourceCard
                      key={index}
                      {...resource}
                      isExpanded={expandedCard === index}
                      onExpand={() =>
                        setExpandedCard(expandedCard === index ? null : index)
                      }
                    />
                  ))}
                </div>
              )}
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="font-display text-h3 text-secondary mb-4">Disclaimer</h3>
              <p className="text-textPrimary">
                These resources are provided for educational purposes. Students for Refugees does not endorse all viewpoints expressed in these links but provides them to encourage research and understanding of the complex issues surrounding refugee crises.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
      
      <Footer />
      <StickyBottomNav />
    </div>
  );
};

export default Africa;